interface SectionHeaderProps {
  /** Eyebrow text — e.g. "01 · How It Works". */
  readonly eyebrow: string;
  /** Headline children — keep the `<br />` tags inline if the prototype uses them. */
  readonly headline: React.ReactNode;
  /** Lead paragraph text. */
  readonly lead: string;
}

/**
 * Shared editorial header — eyebrow + h2 + lead — used at the top of all 4
 * body sections (How It Works / Who It's For / Data Views / Methodology).
 * Mirrors the prototype's `.section-header` block (`_archive/home.html`
 * lines 439–454):
 *
 *   .section-header { margin-bottom: 52px; }       — left-aligned, no width cap
 *   .ed-num         { 11px / 800 / .16em / blue, with a trailing 24x2 rule }
 *   .ed-h2          { clamp(26,2.6vw,36) / 800 / 1.13 / -.02em }
 *   .ed-lead        { 15px / 1.8 / max-width: 560px }
 *
 * Width cap lives on the lead alone — the eyebrow + h2 are free to take
 * their natural width within the section.
 */
export function SectionHeader({ eyebrow, headline, lead }: SectionHeaderProps) {
  return (
    <header className="mb-[52px]">
      <p className="mb-3 inline-flex items-center gap-2.5 text-eyebrow font-extrabold uppercase text-blue-link after:h-0.5 after:w-6 after:rounded-sm after:bg-blue/50 after:content-['']">
        {eyebrow}
      </p>
      <h2
        className="mb-4 font-extrabold leading-[1.13] tracking-[-0.02em] text-text-1"
        style={{ fontSize: 'clamp(26px, 2.6vw, 36px)' }}
      >
        {headline}
      </h2>
      {/* CR-33 (Maria 2026-07-08 evening): lead cap widened 560px → 760px.
          Maria's homepage-width ask covered the SUBSECTION descriptions too
          ("can we increase those as well to decrease the number of lines") —
          the earlier CR-29 pass only touched the hero and left this 560px
          prototype cap in place, so the sections looked unchanged. 760px at
          15px ≈ comfortable long-form measure while still visibly narrower
          than the full section width. */}
      {/* CR-022: lead body copy raised text-[15px] → text-base (16px). */}
      <p className="max-w-lead text-base leading-[1.8] text-text-3">{lead}</p>
    </header>
  );
}
